import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as clinicsApi from '../../api/clinics.api';
import * as payoutsApi from '../../api/payouts.api';
import { errorMessage } from '../../api/client';

// Common Malaysian bank codes (from Curlec Payouts docs — Bank/Wallet
// Codes table). Rendered as a datalist so the admin can type or pick.
const BANK_OPTIONS = [
  { code: 'MBBEMYKL', name: 'Maybank' },
  { code: 'CIBBMYKL', name: 'CIMB Bank' },
  { code: 'PBBEMYKL', name: 'Public Bank' },
  { code: 'RHBBMYKL', name: 'RHB Bank' },
  { code: 'HLBBMYKL', name: 'Hong Leong Bank' },
  { code: 'BIMBMYKL', name: 'Bank Islam' },
  { code: 'ARBKMYKL', name: 'Ambank' },
  { code: 'MFBBMYKL', name: 'Alliance Bank' },
  { code: 'PHBMMYKL', name: 'Affin Bank' },
  { code: 'BKRMMYKL', name: 'Bank Kerjasama Rakyat' },
  { code: 'BSNAMYK1', name: 'Bank Simpanan Nasional' },
  { code: 'AGOBMYKL', name: 'Agrobank' },
  { code: 'RJHIMYKL', name: 'Al Rajhi Bank' },
  { code: 'UOVBMYKL', name: 'UOB Bank' },
  { code: 'HBMBMYKL', name: 'HSBC Bank' },
  { code: 'SCBLMYKX', name: 'Standard Chartered Bank' },
  { code: 'TNGDMYNB', name: 'TNG Digital (TouchNGo)' },
  { code: 'BGPYMYNB', name: 'BigPay Malaysia' },
  { code: 'BOSTMYNB', name: 'Boost' },
  { code: 'GXSPMYKL', name: 'GX Bank' },
];

const ID_TYPES = ['NRIC', 'OLD_IC', 'PASSPORT_NUMBER', 'BUSINESS_REGISTRATION_NUMBER', 'ARMY OR POLICE'];
const PAYMENT_METHODS = [
  { value: '1', label: 'Instant Transfer (IBG)' },
  { value: '2', label: 'DuitNow (Proxy)' },
];

function ClinicPayoutSettings() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [clinic, setClinic] = useState(null);
  const [form, setForm] = useState({
    clinicSharePct: 0,
    autoRelease: false,
    bankCode: '',
    accountNumber: '',
    accountHolderName: '',
    idType: '',
    idValue: '',
    paymentMethod: '1',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await clinicsApi.getClinic(id);
        if (cancelled) return;
        setClinic(c);
        const cp = c?.clinicProfile || {};
        const ps = cp.payoutSettings || {};
        setForm({
          clinicSharePct:    Number(cp.clinicSharePct || 0),
          autoRelease:       Boolean(ps.autoRelease),
          bankCode:          ps.bankCode || '',
          accountNumber:     ps.accountNumber || '',
          accountHolderName: ps.accountHolderName || '',
          idType:            ps.idType || '',
          idValue:           ps.idValue || '',
          paymentMethod:     ps.paymentMethod || '1',
        });
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  function on(k, v) { setForm((f) => ({ ...f, [k]: v })); setOkMsg(''); }

  async function save() {
    setSaving(true);
    setErr('');
    setOkMsg('');
    try {
      await payoutsApi.updateClinicPayoutSettings(id, {
        clinicSharePct:    Number(form.clinicSharePct) || 0,
        autoRelease:       form.autoRelease,
        bankCode:          form.bankCode.trim(),
        accountNumber:     form.accountNumber.trim(),
        accountHolderName: form.accountHolderName.trim(),
        idType:            form.idType.trim(),
        idValue:           form.idValue.trim(),
        paymentMethod:     form.paymentMethod,
      });
      setOkMsg('Settings saved.');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  const preview = Math.max(0, Math.min(100, Number(form.clinicSharePct) || 0));
  const sample = 1559;
  const clinicSample = Math.round((sample * preview) / 100 * 100) / 100;
  const platformSample = Math.round((sample - clinicSample) * 100) / 100;

  return (
    <>
      <PageHeader
        title={`Payout settings — ${clinic?.clinicProfile?.clinicName || clinic?.name || 'Clinic'}`}
        subtitle="Commission percentage this clinic earns on each successful treatment, plus their bank details for automated payouts."
        actions={<Link to="/admin/payouts" className="btn btn-ghost"><ArrowLeft size={16} /> Back to payouts</Link>}
      />

      {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}
      {okMsg && <div className="notice success" style={{ marginBottom: 'var(--s-4)' }}>{okMsg}</div>}

      <div className="grid grid-2" style={{ gap: 'var(--s-4)' }}>
        <div className="card">
          <h3 className="card-title">Commission per successful treatment</h3>
          <div className="form-group">
            <label className="form-label">Commission %</label>
            <input
              type="number" min="0" max="100" step="0.01"
              className="input"
              value={form.clinicSharePct}
              onChange={(e) => on('clinicSharePct', e.target.value)}
            />
            <p className="muted text-sm" style={{ marginTop: 6 }}>
              Percentage of each successfully treated customer's payment that
              is paid out to this clinic. The remainder is retained by the platform.
            </p>
          </div>

          <div className="form-group">
            <label className="form-check">
              <input
                type="checkbox"
                checked={form.autoRelease}
                onChange={(e) => on('autoRelease', e.target.checked)}
              />
              <span>Auto-release payout when the customer confirms service</span>
            </label>
            <p className="muted text-sm" style={{ marginTop: 6 }}>
              When enabled, the payout is dispatched to the clinic's bank the moment
              the customer marks the booking as delivered. When disabled, it waits
              in Pending for a super-admin to manually release.
            </p>
          </div>

          <div className="notice info" style={{ marginTop: 8 }}>
            <strong>Preview.</strong> If a customer pays <strong>RM {sample}</strong>:
            &nbsp;clinic receives <strong>RM {clinicSample}</strong>,
            &nbsp;platform keeps <strong>RM {platformSample}</strong>.
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Clinic bank details</h3>
          <div className="form-group">
            <label className="form-label">Bank</label>
            <input
              className="input"
              list="bank-options"
              value={form.bankCode}
              onChange={(e) => on('bankCode', e.target.value.toUpperCase())}
              placeholder="e.g. MBBEMYKL"
            />
            <datalist id="bank-options">
              {BANK_OPTIONS.map((b) => (
                <option key={b.code} value={b.code}>{b.name}</option>
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label className="form-label">Account number</label>
            <input
              className="input"
              value={form.accountNumber}
              onChange={(e) => on('accountNumber', e.target.value.replace(/\D/g, ''))}
              placeholder="Numeric only, up to 20 digits"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Account holder name</label>
            <input
              className="input"
              value={form.accountHolderName}
              onChange={(e) => on('accountHolderName', e.target.value)}
              placeholder="As per bank record"
            />
          </div>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <div className="form-group">
              <label className="form-label">ID type</label>
              <select className="input" value={form.idType} onChange={(e) => on('idType', e.target.value)}>
                <option value="">— none —</option>
                {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">ID value</label>
              <input
                className="input"
                value={form.idValue}
                onChange={(e) => on('idValue', e.target.value)}
                placeholder="e.g. business reg no."
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Payment method</label>
            <select className="input" value={form.paymentMethod} onChange={(e) => on('paymentMethod', e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--s-4)' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </>
  );
}

export default ClinicPayoutSettings;
